import { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { api, ApiError } from '@/api/client';
import type {
  AgentChatListItem,
  MessengerComposerDraft,
  MessengerDraftTargetKind,
  MessengerMessageSearchResult,
  MessengerThread,
  MessengerMessage,
} from '@/api/client';
import { getSocket } from '@/hooks/useRealtimeSync';
import {
  ensureDirectThreadSlot,
  installDirectThreadSocketBridge,
  resetDirectThreadCache,
  setDirectThreadCacheProtectedId,
  setDirectThreadLikeViewerPersonId,
} from '@/components/Messenger/cache/directThreadCacheStore';
import {
  applyMessengerDraftSocketEvent,
  setMessengerDraftsSnapshot,
} from '@/components/Messenger/cache/messengerDraftsStore';
import { setMessengerThreadsSnapshot, resetMessengerThreadsStore } from '@/components/Messenger/cache/messengerThreadsStore';
import { setMessengerSidebarUnreadViewportSubtract } from '@/components/Messenger/cache/messengerSidebarUnreadViewportStore';
import { setAgentChatProcessingFlag } from '@/components/Messenger/cache/agentChatsProcessingStore';
import { useAnyAgentChatProcessing } from '@/hooks/useAnyAgentChatProcessing';
import { ChatSidebar, type ActiveContact, type ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { AgentChatsSection } from '@/components/Messenger/sidebar/AgentChatsSection';
import { AgentChatView } from '@/components/Messenger/agent/AgentChatView';
import type { ChatModelId } from '@/components/Messenger/agent/agentChatSharedTypes';
import { DirectThreadView } from '@/components/Messenger/direct/DirectThreadView';
import { ForwardMessageModal } from '@/components/Messenger/direct/ForwardMessageModal';
import { GroupThreadView } from '@/components/Messenger/group/GroupThreadView';
import { CreateGroupChatModal } from '@/components/Messenger/group/CreateGroupChatModal';
import { generateUUID } from '@/utils/generateUUID';
import {
  MESSENGER_AGENT_CHATS_INVALIDATE,
  MESSENGER_UNREAD_INVALIDATE,
  invalidateMessengerUnread,
  type MessengerOpenAgentChatFromNotificationDetail,
  type MessengerOpenThreadFromNotificationDetail,
} from '@/constants/messengerEvents';
import { buildMessengerMessagePreviewLine } from '@/lib/messengerNotificationPreview';
import type { MessengerSocketThreadMeta } from '@/lib/messengerNotificationPreview';
import { unwrapMessengerMessageSocketPayload, unwrapMessengerMessagesBatchSocketPayload } from '@/lib/messengerSocketPayload';
import {
  resetMessengerNotificationSurfaceState,
  setMessengerNotificationSurfaceState,
} from '@/notifications/messengerSurfaceState';
import {
  dismissAgentChatNotifications,
  dismissMessengerThreadNotifications,
} from '@/notifications/notificationOrchestrator';
import { MESSENGER_EMPTY_FORWARD_DRAFT } from '@/constants/messengerForwardDraft';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';
import {
  displayedMessengerUnreadForPeer,
  displayedMessengerUnreadForThread,
} from '@/components/Messenger/shared/sidebar/messengerSidebarUnread';
import {
  installPresenceSocketBridge,
  markUserRecentlyActive,
  seedPresenceFromUsers,
  resetPresenceStore,
} from '@/lib/presenceStore';
import '@/components/Messenger/styles/ChatShell.css';
import { ChatShellEmptyPane } from '@/components/Messenger/shell/ChatShellEmptyPane';
import { areAgentChatListItemsEqual } from '@/components/Messenger/shared/agentChatListEquality';

const AGENT_CHATS_SIDEBAR_INITIAL = 3;
/** Подгрузка по кнопке «Показать больше». */
const AGENT_CHATS_SIDEBAR_PAGE = 5;
/** Длительность анимации удаления строки в списке чатов Астры (см. ChatSidebar.css). */
const AGENT_CHAT_SIDEBAR_ROW_EXIT_MS = 200;

/** Как на сервере в agentChatsList: новее выше (last_message_at, иначе created_at). */
function agentChatListActivityMs(c: AgentChatListItem): number {
  const raw = c.last_message_at ?? c.created_at;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function sortAgentChatsNewestFirst(chats: AgentChatListItem[]): AgentChatListItem[] {
  return [...chats].sort((a, b) => {
    const diff = agentChatListActivityMs(b) - agentChatListActivityMs(a);
    if (diff !== 0) return diff;
    return b.id.localeCompare(a.id);
  });
}

interface ChatShellProps {
  onClose?: () => void;
  /** Пока false — не помечаем direct-чаты прочитанными (закрытие/анимация, фон). */
  messengerReadReceiptsActive?: boolean;
  /** Пока false — поповер скрыт/уходит; лента догоняет низ при повторном показе (см. ChatView). */
  messengerSurfaceVisible?: boolean;
  /** Открыть тред после клика по уведомлению (сбрасывается родителем после обработки). */
  openThreadIntent?: MessengerOpenThreadFromNotificationDetail | null;
  onConsumedOpenThreadIntent?: () => void;
  /** Открыть конкретный чат Астры после уведомления. */
  openAgentChatIntent?: MessengerOpenAgentChatFromNotificationDetail | null;
  onConsumedOpenAgentChatIntent?: () => void;
}

function threadPreviewFromMessage(msg: MessengerMessage): string {
  return buildMessengerMessagePreviewLine(msg);
}

function messengerThreadActivityMs(thread: MessengerThread): number {
  const raw = thread.last_message_at;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function compareThreadsByActivityDesc(a: MessengerThread, b: MessengerThread): number {
  const diff = messengerThreadActivityMs(b) - messengerThreadActivityMs(a);
  if (diff !== 0) return diff;
  return a.id.localeCompare(b.id);
}

function areMessengerThreadsEqual(a: MessengerThread, b: MessengerThread): boolean {
  return (
    a.id === b.id &&
    a.thread_kind === b.thread_kind &&
    (a.title ?? null) === (b.title ?? null) &&
    (a.last_message_at ?? null) === (b.last_message_at ?? null) &&
    (a.last_message_preview ?? null) === (b.last_message_preview ?? null) &&
    (a.last_message_sender_id ?? null) === (b.last_message_sender_id ?? null) &&
    (a.last_message_sender_name ?? null) === (b.last_message_sender_name ?? null) &&
    a.unread_count === b.unread_count &&
    (a.peer_id ?? null) === (b.peer_id ?? null) &&
    (a.peer_name ?? null) === (b.peer_name ?? null) &&
    Boolean(a.is_saved) === Boolean(b.is_saved) &&
    (a.my_member_role ?? 'member') === (b.my_member_role ?? 'member')
  );
}

function areChatUsersEqual(a: ChatContactUser, b: ChatContactUser): boolean {
  return (
    a.id === b.id &&
    (a.name ?? null) === (b.name ?? null) &&
    (a.first_name ?? null) === (b.first_name ?? null) &&
    (a.last_name ?? null) === (b.last_name ?? null) &&
    (a.access ?? null) === (b.access ?? null) &&
    (a.role ?? null) === (b.role ?? null)
  );
}

function mergeStableListSnapshot<T>(
  prev: T[],
  incoming: T[],
  getId: (item: T) => string,
  areEqual: (a: T, b: T) => boolean
): T[] {
  if (prev.length === 0) return incoming;

  const prevById = new Map(prev.map((item) => [getId(item), item]));
  let changed = prev.length !== incoming.length;

  const next = incoming.map((item, index) => {
    const prevItem = prevById.get(getId(item));
    if (prevItem && areEqual(prevItem, item)) {
      if (prev[index] !== prevItem) changed = true;
      return prevItem;
    }
    changed = true;
    return item;
  });

  if (!changed) {
    for (let i = 0; i < prev.length; i += 1) {
      if (prev[i] !== next[i]) {
        changed = true;
        break;
      }
    }
  }

  return changed ? next : prev;
}

function mergeStableThreads(prev: MessengerThread[], incoming: MessengerThread[]): MessengerThread[] {
  const sorted = [...incoming].sort(compareThreadsByActivityDesc);
  return mergeStableListSnapshot(prev, sorted, (thread) => thread.id, areMessengerThreadsEqual);
}

function mergeStableUsers(prev: ChatContactUser[], incoming: ChatContactUser[]): ChatContactUser[] {
  return mergeStableListSnapshot(prev, incoming, (user) => user.id, areChatUsersEqual);
}

function mergeStableAgentChats(
  prev: AgentChatListItem[],
  incoming: AgentChatListItem[]
): AgentChatListItem[] {
  const sorted = sortAgentChatsNewestFirst(incoming);
  return mergeStableListSnapshot(prev, sorted, (c) => c.id, areAgentChatListItemsEqual);
}

function upsertThreadWithStableOrder(
  threads: MessengerThread[],
  nextThread: MessengerThread
): MessengerThread[] {
  const nextList = threads.some((thread) => thread.id === nextThread.id)
    ? threads.map((thread) => (thread.id === nextThread.id ? nextThread : thread))
    : [...threads, nextThread];
  return mergeStableThreads(threads, nextList);
}

function applyIncomingMessageToThreads(
  threads: MessengerThread[],
  msg: MessengerMessage,
  currentUserId: string | null,
  activeThreadId: string | null,
  threadMeta: MessengerSocketThreadMeta | null
): MessengerThread[] {
  const existing = threads.find((thread) => thread.id === msg.thread_id);
  const isOwnMsg = msg.sender_id === currentUserId;
  const isActiveThread = msg.thread_id === activeThreadId;
  const preview = threadPreviewFromMessage(msg);
  const base: MessengerThread | null =
    existing ??
    (threadMeta
      ? {
          id: msg.thread_id,
          thread_kind: threadMeta.thread_kind,
          title: threadMeta.title ?? null,
          last_message_at: null,
          last_message_preview: null,
          unread_count: 0,
          peer_id: threadMeta.peer_id ?? null,
          peer_name: threadMeta.peer_name ?? null,
          is_saved: false,
        }
      : null);

  if (!base) return threads;

  const threadKind = threadMeta?.thread_kind ?? base.thread_kind;
  const peerId = threadKind === 'direct' ? threadMeta?.peer_id ?? base.peer_id ?? null : null;
  const peerName = threadKind === 'direct' ? threadMeta?.peer_name ?? base.peer_name ?? null : null;

  const groupLastSender =
    threadKind === 'group'
      ? {
          last_message_sender_id: msg.sender_id,
          last_message_sender_name: msg.sender_display_name?.trim() || null,
        }
      : {};

  const nextThread: MessengerThread = {
    ...base,
    thread_kind: threadKind,
    title: threadMeta?.title ?? base.title ?? null,
    peer_id: peerId,
    peer_name: peerName,
    last_message_at: msg.created_at,
    last_message_preview: preview || base.last_message_preview || null,
    unread_count: !isOwnMsg && !isActiveThread ? base.unread_count + 1 : base.unread_count,
    ...groupLastSender,
  };

  return upsertThreadWithStableOrder(threads, nextThread);
}

function applyIncomingMessagesBatchToThreads(
  threads: MessengerThread[],
  messages: MessengerMessage[],
  currentUserId: string | null,
  activeThreadId: string | null,
  threadMeta: MessengerSocketThreadMeta | null
): MessengerThread[] {
  if (messages.length === 0) return threads;
  const ordered = [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const tid = ordered[0]!.thread_id;
  if (!tid || !ordered.every((m) => m.thread_id === tid)) return threads;

  const last = ordered[ordered.length - 1]!;
  const existing = threads.find((thread) => thread.id === tid);
  const isActiveThread = tid === activeThreadId;
  const incrementOthers = isActiveThread
    ? 0
    : ordered.filter((m) => m.sender_id !== currentUserId).length;

  const preview = threadPreviewFromMessage(last);
  const base: MessengerThread | null =
    existing ??
    (threadMeta
      ? {
          id: tid,
          thread_kind: threadMeta.thread_kind,
          title: threadMeta.title ?? null,
          last_message_at: null,
          last_message_preview: null,
          unread_count: 0,
          peer_id: threadMeta.peer_id ?? null,
          peer_name: threadMeta.peer_name ?? null,
          is_saved: false,
        }
      : null);

  if (!base) return threads;

  const threadKind = threadMeta?.thread_kind ?? base.thread_kind;
  const peerId = threadKind === 'direct' ? threadMeta?.peer_id ?? base.peer_id ?? null : null;
  const peerName = threadKind === 'direct' ? threadMeta?.peer_name ?? base.peer_name ?? null : null;

  const groupLastSender =
    threadKind === 'group'
      ? {
          last_message_sender_id: last.sender_id,
          last_message_sender_name: last.sender_display_name?.trim() || null,
        }
      : {};

  const nextThread: MessengerThread = {
    ...base,
    thread_kind: threadKind,
    title: threadMeta?.title ?? base.title ?? null,
    peer_id: peerId,
    peer_name: peerName,
    last_message_at: last.created_at,
    last_message_preview: preview || base.last_message_preview || null,
    unread_count: base.unread_count + incrementOthers,
    ...groupLastSender,
  };

  return upsertThreadWithStableOrder(threads, nextThread);
}

function buildLocalThreadFromActiveContact(
  msg: MessengerMessage,
  activeContact: ActiveContact | null,
  currentUserId: string | null
): MessengerThread | null {
  if (activeContact === 'saved') {
    return {
      id: msg.thread_id,
      thread_kind: 'direct',
      title: null,
      last_message_at: msg.created_at,
      last_message_preview: threadPreviewFromMessage(msg),
      unread_count: 0,
      peer_id: currentUserId,
      peer_name: 'Сохранённые сообщения',
      is_saved: true,
    };
  }

  if (activeContact === 'agent' || activeContact == null) {
    return null;
  }

  if (activeContact.type === 'group') {
    return {
      id: msg.thread_id,
      thread_kind: 'group',
      title: activeContact.title,
      last_message_at: msg.created_at,
      last_message_preview: threadPreviewFromMessage(msg),
      last_message_sender_id: msg.sender_id,
      last_message_sender_name: msg.sender_display_name?.trim() || null,
      unread_count: 0,
      my_member_role: 'member',
    };
  }

  return {
    id: msg.thread_id,
    thread_kind: 'direct',
    title: null,
    last_message_at: msg.created_at,
    last_message_preview: threadPreviewFromMessage(msg),
    unread_count: 0,
    peer_id: activeContact.peerId,
    peer_name: activeContact.peerName,
    is_saved: false,
  };
}

function applyLocalCommittedMessageToThreads(
  threads: MessengerThread[],
  msg: MessengerMessage,
  activeContact: ActiveContact | null,
  currentUserId: string | null
): MessengerThread[] {
  const existing = threads.find((thread) => thread.id === msg.thread_id);
  const base = existing ?? buildLocalThreadFromActiveContact(msg, activeContact, currentUserId);
  if (!base) return threads;

  const nextThread: MessengerThread = {
    ...base,
    last_message_at: msg.created_at,
    last_message_preview: threadPreviewFromMessage(msg),
    ...(base.thread_kind === 'group'
      ? {
          last_message_sender_id: msg.sender_id,
          last_message_sender_name: msg.sender_display_name?.trim() || null,
        }
      : {}),
  };

  return upsertThreadWithStableOrder(threads, nextThread);
}

export function ChatShell({
  onClose,
  messengerReadReceiptsActive = false,
  messengerSurfaceVisible = true,
  openThreadIntent = null,
  onConsumedOpenThreadIntent,
  openAgentChatIntent = null,
  onConsumedOpenAgentChatIntent,
}: ChatShellProps) {
  const { user } = useAuth();
  const { showError } = useToast();
  const currentUserId = user?.id ?? null;

  const [sidebarUnreadSubtractCount, setSidebarUnreadSubtractCount] = useState(0);
  /**
   * serverChatId — id строки agent_chats после первого сообщения; null = черновик.
   * uiSessionId — ключ экземпляра useChat в панели (аналог «открытого треда» в памяти оболочки).
   */
  const [agentUiSessionId, setAgentUiSessionId] = useState(() => generateUUID());
  const [agentServerChatId, setAgentServerChatId] = useState<string | null>(null);
  const [agentChatRows, setAgentChatRows] = useState<AgentChatListItem[]>([]);
  const [agentTotalUnread, setAgentTotalUnread] = useState(0);
  const [agentProcessingByChatId, setAgentProcessingByChatId] = useState<Record<string, boolean>>({});
  const [agentChatsHasMore, setAgentChatsHasMore] = useState(false);
  const [agentChatsNextCursor, setAgentChatsNextCursor] = useState<{ iso: string; id: string } | null>(null);
  const [agentChatsLoading, setAgentChatsLoading] = useState(false);
  const [agentChatsLoadingMore, setAgentChatsLoadingMore] = useState(false);
  const [agentChatsSectionOpen, setAgentChatsSectionOpen] = useState(false);
  const [exitingAgentChatId, setExitingAgentChatId] = useState<string | null>(null);
  const [threads, setThreads] = useState<MessengerThread[]>([]);
  const [users, setUsers] = useState<ChatContactUser[]>([]);
  const [activeContact, setActiveContact] = useState<ActiveContact | null>(null);
  const [savedThread, setSavedThread] = useState<MessengerThread | null>(null);
  const [savedThreadLoading, setSavedThreadLoading] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [messageJumpIntent, setMessageJumpIntent] = useState<{
    threadId: string;
    messageId: string;
    nonce: number;
  } | null>(null);

  // Пересылка сообщений
  const [forwardMsg, setForwardMsg] = useState<MessengerMessage | null>(null);
  const [forwardMsgsModal, setForwardMsgsModal] = useState<MessengerMessage[]>([]);
  const [pendingForwardsByThread, setPendingForwardsByThread] = useState<Record<string, MessengerMessage[]>>({});

  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contactsFetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openThreadIntentFetchPassRef = useRef(0);
  const forwardOriginThreadIdRef = useRef<string | null>(null);
  const forwardClearNonceRef = useRef(0);
  const agentServerChatIdRef = useRef<string | null>(null);
  const agentChatRowsRef = useRef<AgentChatListItem[]>([]);
  /** После ухода с Астры на другой контакт — при возврате на Астру не сбрасывать сессию (только память вкладки). */
  const restoreAgentShellSessionRef = useRef(false);
  const prevActiveContactRef = useRef<ActiveContact | null>(null);
  const messageJumpNonceRef = useRef(0);
  const [forwardSelectionClearSignal, setForwardSelectionClearSignal] = useState<{
    nonce: number;
    threadId: string;
  } | null>(null);

  const [messengerSidebarSearchHighlightWords, setMessengerSidebarSearchHighlightWords] = useState<
    readonly string[]
  >(MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY);

  useEffect(() => {
    agentServerChatIdRef.current = agentServerChatId;
  }, [agentServerChatId]);

  useEffect(() => {
    agentChatRowsRef.current = agentChatRows;
  }, [agentChatRows]);

  useEffect(() => {
    setMessengerThreadsSnapshot(threads);
  }, [threads]);

  const setAgentChatProcessing = useCallback((chatId: string, processing: boolean) => {
    const normalized = chatId.trim();
    if (!normalized) return;
    setAgentChatProcessingFlag(normalized, processing);
    setAgentProcessingByChatId((prev) => {
      const was = Boolean(prev[normalized]);
      if (processing && was) return prev;
      if (!processing && !was) return prev;
      const next = { ...prev };
      if (processing) next[normalized] = true;
      else delete next[normalized];
      return next;
    });
  }, []);

  const syncAgentProcessingFromRows = useCallback((rows: AgentChatListItem[]) => {
    setAgentProcessingByChatId((prev) => {
      let next: Record<string, boolean> | null = null;
      for (const row of rows) {
        const id = row.id.trim();
        if (!id) continue;
        const want = Boolean(row.processing);
        const has = Boolean((next ?? prev)[id]);
        if (want === has) continue;
        if (!next) next = { ...prev };
        if (want) next[id] = true;
        else delete next[id];
      }
      return next ?? prev;
    });
  }, []);

  useEffect(() => {
    const prev = prevActiveContactRef.current;
    if (prev === 'agent' && activeContact !== 'agent') {
      restoreAgentShellSessionRef.current = true;
    }
    prevActiveContactRef.current = activeContact;
  }, [activeContact]);

  const handleMessengerSidebarSearchHighlightWords = useCallback((words: readonly string[]) => {
    setMessengerSidebarSearchHighlightWords((prev) => {
      if (prev.length !== words.length) return words;
      for (let i = 0; i < prev.length; i += 1) {
        if (prev[i] !== words[i]) return words;
      }
      return prev;
    });
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.messenger.getThreads();
      setThreads((prev) => mergeStableThreads(prev, res.threads));
    } catch {
      /* сохраняем текущий снимок списка */
    }
  }, []);

  const debouncedFetchThreads = useCallback(() => {
    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    fetchDebounceRef.current = setTimeout(() => {
      fetchDebounceRef.current = null;
      void fetchThreads();
    }, 500);
  }, [fetchThreads]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.users.list(undefined, { limit: 1000 });
      const items = res.items ?? [];
      setUsers((prev) =>
        mergeStableUsers(
          prev,
          items
            .filter((u) => u.id !== currentUserId)
            .map((u) => ({
              id: u.id,
              name: u.name ?? undefined,
              first_name: u.first_name,
              last_name: u.last_name,
              access: u.access ?? null,
              role: u.role ?? null,
            }))
        )
      );
      seedPresenceFromUsers(items);
    } catch {
      /* сохраняем текущий снимок контактов */
    }
  }, [currentUserId]);

  const debouncedFetchUsers = useCallback(() => {
    if (contactsFetchDebounceRef.current) clearTimeout(contactsFetchDebounceRef.current);
    contactsFetchDebounceRef.current = setTimeout(() => {
      contactsFetchDebounceRef.current = null;
      void fetchUsers();
    }, 500);
  }, [fetchUsers]);

  const fetchAgentChatsFirst = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setAgentChatsLoading(true);
    try {
      const res = await api.agentChats.list({ limit: AGENT_CHATS_SIDEBAR_INITIAL });
      const apply = () => {
        syncAgentProcessingFromRows(res.chats);
        setAgentChatRows((p) => mergeStableAgentChats(p, res.chats));
        setAgentTotalUnread(res.total_unread ?? 0);
        setAgentChatsHasMore(res.has_more);
        setAgentChatsNextCursor(res.next_cursor);
      };
      if (silent) {
        startTransition(apply);
      } else {
        apply();
      }
    } catch {
      /* оставляем список */
    } finally {
      if (!silent) setAgentChatsLoading(false);
    }
  }, [syncAgentProcessingFromRows]);

  const refreshAgentChatsListQuiet = useCallback(() => {
    void fetchAgentChatsFirst({ silent: true });
  }, [fetchAgentChatsFirst]);

  useEffect(() => {
    /** Гидратация processing/unread для вкладки «Астра» сразу после reload, до открытия секции. */
    void fetchAgentChatsFirst({ silent: true });
  }, [fetchAgentChatsFirst]);

  const fetchAgentChatsMore = useCallback(async () => {
    const cur = agentChatsNextCursor;
    if (!cur) return;
    setAgentChatsLoadingMore(true);
    try {
      const res = await api.agentChats.list({
        limit: AGENT_CHATS_SIDEBAR_PAGE,
        cursor_iso: cur.iso,
        cursor_id: cur.id,
      });
      syncAgentProcessingFromRows(res.chats);
      setAgentChatRows((prev) => mergeStableAgentChats(prev, [...prev, ...res.chats]));
      setAgentTotalUnread(res.total_unread ?? 0);
      setAgentChatsHasMore(res.has_more);
      setAgentChatsNextCursor(res.next_cursor);
    } catch {
      /* ignore */
    } finally {
      setAgentChatsLoadingMore(false);
    }
  }, [agentChatsNextCursor, syncAgentProcessingFromRows]);

  const selectPersistedAgentChat = useCallback((id: string) => {
    setActiveContact('agent');
    setAgentServerChatId(id);
    setAgentUiSessionId(id);
  }, []);

  const handleDeleteAgentChat = useCallback(async (id: string) => {
    const removed = agentChatRowsRef.current.find((c) => c.id === id);
    setExitingAgentChatId(id);
    try {
      await api.agentChats.delete(id);
      window.setTimeout(() => {
        startTransition(() => {
          setAgentChatRows((prev) => prev.filter((c) => c.id !== id));
          if (removed && (removed.unread_count ?? 0) > 0) {
            setAgentTotalUnread((total) =>
              Math.max(0, total - (removed.unread_count ?? 0))
            );
          }
          setAgentProcessingByChatId((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
          });
          if (agentServerChatIdRef.current === id) {
            setAgentServerChatId(null);
            setAgentUiSessionId(generateUUID());
          }
          setExitingAgentChatId(null);
        });
      }, AGENT_CHAT_SIDEBAR_ROW_EXIT_MS);
    } catch {
      setExitingAgentChatId(null);
    }
  }, []);

  const handleRenameAgentChat = useCallback(
    async (id: string, defaultTitle: string) => {
      const next = window.prompt('Название чата', defaultTitle.trim() || 'Новый чат');
      if (next === null) return;
      try {
        const res = await api.agentChats.patch(id, { title: next });
        setAgentChatRows((prev) =>
          prev.map((c) => (c.id === id ? { ...c, title: res.title, updated_at: res.updated_at } : c))
        );
      } catch (err) {
        const msg =
          err instanceof ApiError && err.message.trim() ? err.message : 'Не удалось переименовать чат';
        showError(msg);
      }
    },
    [showError]
  );

  const agentOpenPersistedTitle = useMemo((): string | null => {
    if (!agentServerChatId) return null;
    const row = agentChatRows.find((c) => c.id === agentServerChatId);
    const t = row?.title?.trim();
    return t ? t : null;
  }, [agentServerChatId, agentChatRows]);

  const agentOpenPersistedModelSlot = useMemo((): ChatModelId | null => {
    if (!agentServerChatId) return null;
    const row = agentChatRows.find((c) => c.id === agentServerChatId);
    if (!row) return null;
    const lastSlot = row.last_model_slot;
    if (lastSlot === 'premium' || lastSlot === 'premium_pro') return lastSlot;
    if (lastSlot === 'slow') return 'premium';
    return null;
  }, [agentServerChatId, agentChatRows]);

  useEffect(() => {
    void fetchThreads();
    void fetchUsers();
    void api.messenger
      .getDrafts()
      .then((response) => {
        setMessengerDraftsSnapshot(response.drafts);
      })
      .catch(() => {
        /* сохраняем текущий снимок черновиков */
      });
  }, [fetchThreads, fetchUsers]);

  useEffect(() => {
    return installDirectThreadSocketBridge();
  }, []);


  useEffect(() => {
    setDirectThreadLikeViewerPersonId(
      currentUserId,
      user
        ? {
            first_name: user.first_name?.trim() ? user.first_name : null,
            last_name: user.last_name?.trim() ? user.last_name : null,
          }
        : null
    );
    return () => setDirectThreadLikeViewerPersonId(null);
  }, [currentUserId, user]);

  useEffect(() => {
    return installPresenceSocketBridge();
  }, []);

  useEffect(() => {
    const id =
      activeContact !== 'agent' &&
      activeContact !== 'saved' &&
      activeContact !== null &&
      'threadId' in activeContact &&
      activeContact.threadId
        ? activeContact.threadId
        : activeContact === 'saved' && savedThread
        ? savedThread.id
        : null;
    setDirectThreadCacheProtectedId(id);
  }, [activeContact, savedThread]);

  const activeThreadId =
    activeContact === 'saved'
      ? savedThread?.id ?? null
      : activeContact !== 'agent' && activeContact !== null && 'threadId' in activeContact
      ? activeContact.threadId
      : null;
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  const handleThreadMessageCommitted = useCallback(
    (msg: MessengerMessage) => {
      setThreads((prev) =>
        applyLocalCommittedMessageToThreads(prev, msg, activeContact, currentUserId)
      );
      if (savedThread?.id === msg.thread_id) {
        setSavedThread((prev) =>
          prev
            ? {
                ...prev,
                last_message_at: msg.created_at,
                last_message_preview: threadPreviewFromMessage(msg),
              }
            : prev
        );
      }
    },
    [activeContact, currentUserId, savedThread?.id]
  );

  useEffect(() => {
    const socket = getSocket();

    const onMessage = (raw: unknown) => {
      const { message: msg, thread } = unwrapMessengerMessageSocketPayload(raw);
      if (msg.sender_id && msg.sender_id !== currentUserIdRef.current) {
        markUserRecentlyActive(msg.sender_id, msg.created_at);
      }
      let shouldRefetchThreads = false;
      setThreads((prev) => {
        const next = applyIncomingMessageToThreads(
          prev,
          msg,
          currentUserIdRef.current,
          activeThreadIdRef.current,
          thread
        );
        if (next === prev && !prev.some((threadRow) => threadRow.id === msg.thread_id)) {
          shouldRefetchThreads = true;
        }
        return next;
      });
      if (shouldRefetchThreads) {
        debouncedFetchThreads();
      }
    };

    const onMessagesBatch = (raw: unknown) => {
      const parsed = unwrapMessengerMessagesBatchSocketPayload(raw);
      if (!parsed) return;
      const { messages, thread } = parsed;
      for (const msg of messages) {
        if (msg.sender_id && msg.sender_id !== currentUserIdRef.current) {
          markUserRecentlyActive(msg.sender_id, msg.created_at);
        }
      }
      let shouldRefetchThreads = false;
      setThreads((prev) => {
        const next = applyIncomingMessagesBatchToThreads(
          prev,
          messages,
          currentUserIdRef.current,
          activeThreadIdRef.current,
          thread
        );
        const tid = messages[0]?.thread_id;
        if (tid && next === prev && !prev.some((threadRow) => threadRow.id === tid)) {
          shouldRefetchThreads = true;
        }
        return next;
      });
      if (shouldRefetchThreads) {
        debouncedFetchThreads();
      }
    };

    const onMessageEdited = () => {
      debouncedFetchThreads();
    };

    const onMessageDeleted = () => {
      debouncedFetchThreads();
    };

    const onThreadsRefresh = (data?: { threadId?: string; removed?: boolean }) => {
      if (data?.removed && data.threadId) {
        setThreads((prev) => prev.filter((thread) => thread.id !== data.threadId));
        setActiveContact((prev) => {
          if (prev === 'agent' || prev === 'saved' || prev == null) return prev;
          if (prev.threadId === data.threadId) return null;
          return prev;
        });
      }
      debouncedFetchThreads();
    };

    const onContactsRefresh = () => {
      debouncedFetchUsers();
    };

    const onAgentChatUpdated = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const o = raw as { id?: unknown; title?: unknown; updated_at?: unknown };
      if (typeof o.id !== 'string' || !o.id.trim()) return;
      const id = o.id.trim();
      const title = typeof o.title === 'string' ? o.title.trim() : '';
      if (!title) return;
      const updatedAt =
        typeof o.updated_at === 'string' && o.updated_at.trim()
          ? o.updated_at.trim()
          : new Date().toISOString();
      setAgentChatRows((prev) => {
        const idx = prev.findIndex((c) => c.id === id);
        if (idx === -1) {
          const stub: AgentChatListItem = {
            id,
            title,
            summary: null,
            last_message_at: updatedAt,
            last_model_slot: null,
            unread_count: 0,
            created_at: updatedAt,
            updated_at: updatedAt,
          };
          return mergeStableAgentChats(prev, sortAgentChatsNewestFirst([stub, ...prev.filter((c) => c.id !== id)]));
        }
        const old = prev[idx];
        if (old.title === title && old.updated_at === updatedAt) return prev;
        const next = [...prev];
        next[idx] = { ...old, title, updated_at: updatedAt };
        return next;
      });
    };

    const onAgentChatMessage = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const o = raw as { chat_id?: unknown };
      const chatId = typeof o.chat_id === 'string' ? o.chat_id.trim() : '';
      if (!chatId) return;
      setAgentChatProcessing(chatId, false);
      void fetchAgentChatsFirst({ silent: true });
    };

    const onAgentChatRead = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const o = raw as { chat_id?: unknown };
      const chatId = typeof o.chat_id === 'string' ? o.chat_id.trim() : '';
      if (!chatId) return;
      dismissAgentChatNotifications(chatId);
      void fetchAgentChatsFirst({ silent: true });
    };

    const onThreadRead = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const o = raw as { threadId?: unknown; thread_id?: unknown };
      const threadId =
        typeof o.threadId === 'string'
          ? o.threadId.trim()
          : typeof o.thread_id === 'string'
            ? o.thread_id.trim()
            : '';
      if (!threadId) return;
      dismissMessengerThreadNotifications(threadId);
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.id === threadId);
        if (idx < 0) return prev;
        if (prev[idx].unread_count === 0) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], unread_count: 0 };
        return next;
      });
      invalidateMessengerUnread();
    };

    const onDraft = (payload: {
      target_kind: MessengerDraftTargetKind;
      target_id: string;
      draft: MessengerComposerDraft | null;
      updated_at?: string;
    }) => {
      applyMessengerDraftSocketEvent(payload);
    };

    socket.on('messenger:message', onMessage);
    socket.on('messenger:messages_batch', onMessagesBatch);
    socket.on('messenger:message_edited', onMessageEdited);
    socket.on('messenger:message_deleted', onMessageDeleted);
    socket.on('messenger:threads_refresh', onThreadsRefresh);
    socket.on('messenger:contacts_refresh', onContactsRefresh);
    socket.on('messenger:agent_chat_updated', onAgentChatUpdated);
    socket.on('messenger:agent_chat_message', onAgentChatMessage);
    socket.on('messenger:agent_chat_read', onAgentChatRead);
    socket.on('messenger:thread_read', onThreadRead);
    socket.on('messenger:draft', onDraft);
    return () => {
      socket.off('messenger:message', onMessage);
      socket.off('messenger:messages_batch', onMessagesBatch);
      socket.off('messenger:message_edited', onMessageEdited);
      socket.off('messenger:message_deleted', onMessageDeleted);
      socket.off('messenger:threads_refresh', onThreadsRefresh);
      socket.off('messenger:contacts_refresh', onContactsRefresh);
      socket.off('messenger:agent_chat_updated', onAgentChatUpdated);
      socket.off('messenger:agent_chat_message', onAgentChatMessage);
      socket.off('messenger:agent_chat_read', onAgentChatRead);
      socket.off('messenger:thread_read', onThreadRead);
      socket.off('messenger:draft', onDraft);
    };
  }, [debouncedFetchThreads, debouncedFetchUsers, fetchAgentChatsFirst, setAgentChatProcessing]);

  useEffect(() => {
    const onLocalRead = () => debouncedFetchThreads();
    window.addEventListener(MESSENGER_UNREAD_INVALIDATE, onLocalRead);
    return () => window.removeEventListener(MESSENGER_UNREAD_INVALIDATE, onLocalRead);
  }, [debouncedFetchThreads]);

  useEffect(() => {
    const onAgentChatsInvalidate = () => {
      void fetchAgentChatsFirst({ silent: true });
    };
    window.addEventListener(MESSENGER_AGENT_CHATS_INVALIDATE, onAgentChatsInvalidate);
    return () => window.removeEventListener(MESSENGER_AGENT_CHATS_INVALIDATE, onAgentChatsInvalidate);
  }, [fetchAgentChatsFirst]);

  useEffect(() => {
    return () => {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      if (contactsFetchDebounceRef.current) clearTimeout(contactsFetchDebounceRef.current);
      resetDirectThreadCache();
      resetMessengerThreadsStore();
      resetPresenceStore();
      resetMessengerNotificationSurfaceState();
    };
  }, []);

  useEffect(() => {
    setMessengerNotificationSurfaceState(messengerSurfaceVisible, activeThreadId, {
      agentPaneActive: activeContact === 'agent',
      activeAgentChatId: activeContact === 'agent' ? agentServerChatId : null,
    });
  }, [messengerSurfaceVisible, activeThreadId, activeContact, agentServerChatId]);

  useEffect(() => {
    if (activeContact !== 'agent') {
      setAgentChatsSectionOpen(false);
    }
  }, [activeContact]);

  const ensureSavedThread = useCallback(async (): Promise<MessengerThread | null> => {
    if (savedThread) return savedThread;
    if (savedThreadLoading) return null;
    setSavedThreadLoading(true);
    try {
      const thread = await api.messenger.getSavedThread();
      setSavedThread(thread);
      ensureDirectThreadSlot(thread.id);
      await fetchThreads();
      return thread;
    } catch {
      return null;
    } finally {
      setSavedThreadLoading(false);
    }
  }, [savedThread, savedThreadLoading, fetchThreads]);

  const handleSelectContact = useCallback(
    async (contact: ActiveContact) => {
      if (contact === 'agent' && activeContact === 'agent') {
        setAgentChatsSectionOpen((open) => !open);
        return;
      }
      /** Сброс «перехода к сообщению» при обычной навигации; для поиска по сообщению intent выставляется сразу после вызова handleSelectContact. */
      setMessageJumpIntent(null);
      setActiveContact(contact);

      if (contact === 'saved') {
        const thread = await ensureSavedThread();
        if (thread) ensureDirectThreadSlot(thread.id);
        return;
      }

      if (contact === 'agent') {
        setAgentChatsSectionOpen(true);
        if (activeContact !== 'agent') {
          if (!restoreAgentShellSessionRef.current) {
            setAgentServerChatId(null);
            setAgentUiSessionId(generateUUID());
          }
        }
        return;
      }

      if (contact.type === 'group') {
        ensureDirectThreadSlot(contact.threadId);
        return;
      }

      if (contact.type === 'direct' && contact.threadId) {
        ensureDirectThreadSlot(contact.threadId);
      }
      if (contact.type === 'direct' && !contact.threadId) {
        try {
          const thread = await api.messenger.createThreadWithPeer(contact.peerId);
          ensureDirectThreadSlot(thread.id);
          setActiveContact({
            type: 'direct',
            peerId: contact.peerId,
            threadId: thread.id,
            peerName: thread.peer_name ?? contact.peerName,
          });
          await fetchThreads();
        } catch {
          setActiveContact(contact);
        }
      }
    },
    [fetchThreads, ensureSavedThread, activeContact]
  );

  const handleSelectMessageSearchResult = useCallback(
    (result: MessengerMessageSearchResult) => {
      const bumpJumpIntent = () => {
        messageJumpNonceRef.current += 1;
        const nonce = messageJumpNonceRef.current;
        setMessageJumpIntent({
          threadId: result.thread_id,
          messageId: result.message.id,
          nonce,
        });
      };

      if (result.is_saved) {
        void handleSelectContact('saved');
        bumpJumpIntent();
        return;
      }

      if (result.thread_kind === 'group') {
        void handleSelectContact({
          type: 'group',
          threadId: result.thread_id,
          title: result.title?.trim() || 'Группа',
        });
        bumpJumpIntent();
        return;
      }

      const thread = threads.find((t) => t.id === result.thread_id);
      const peerId = result.peer_id ?? thread?.peer_id ?? null;
      if (!peerId) {
        showError('Не удалось открыть чат: нет данных собеседника');
        return;
      }

      void handleSelectContact({
        type: 'direct',
        threadId: result.thread_id,
        peerId,
        peerName: result.peer_name ?? thread?.peer_name ?? 'Чат',
      });
      bumpJumpIntent();
    },
    [handleSelectContact, threads, showError]
  );

  const handleJumpToMessageSettled = useCallback((nonce: number) => {
    setMessageJumpIntent((prev) => (prev?.nonce === nonce ? null : prev));
  }, []);

  useEffect(() => {
    if (!openThreadIntent) {
      openThreadIntentFetchPassRef.current = 0;
      return;
    }

    const run = async () => {
      const { threadId, threadKind, peerId, peerName, title } = openThreadIntent;

      if (threadKind === 'group') {
        const label = (title ?? '').trim() || 'Группа';
        openThreadIntentFetchPassRef.current = 0;
        await handleSelectContact({ type: 'group', threadId, title: label });
        onConsumedOpenThreadIntent?.();
        return;
      }

      const row = threads.find((t) => t.id === threadId);
      const resolvedPeerId = peerId ?? row?.peer_id ?? null;
      const resolvedPeerName =
        peerName ??
        row?.peer_name ??
        'Чат';

      if (!resolvedPeerId) {
        if (openThreadIntentFetchPassRef.current >= 1) {
          openThreadIntentFetchPassRef.current = 0;
          onConsumedOpenThreadIntent?.();
          return;
        }
        openThreadIntentFetchPassRef.current += 1;
        await fetchThreads();
        return;
      }

      openThreadIntentFetchPassRef.current = 0;
      await handleSelectContact({
        type: 'direct',
        threadId,
        peerId: resolvedPeerId,
        peerName: resolvedPeerName,
      });
      onConsumedOpenThreadIntent?.();
    };

    void run();
  }, [
    openThreadIntent,
    threads,
    handleSelectContact,
    fetchThreads,
    onConsumedOpenThreadIntent,
  ]);

  useEffect(() => {
    if (!openAgentChatIntent) return;
    const chatId = openAgentChatIntent.chatId?.trim();
    if (!chatId) {
      onConsumedOpenAgentChatIntent?.();
      return;
    }
    setActiveContact('agent');
    setAgentChatsSectionOpen(true);
    setAgentServerChatId(chatId);
    setAgentUiSessionId(chatId);
    onConsumedOpenAgentChatIntent?.();
  }, [openAgentChatIntent, onConsumedOpenAgentChatIntent]);

  const handleGroupThreadRemoved = useCallback(
    (removedThreadId: string) => {
      setActiveContact((prev) => {
        if (
          prev !== null &&
          prev !== 'agent' &&
          prev !== 'saved' &&
          prev.type === 'group' &&
          prev.threadId === removedThreadId
        ) {
          return null;
        }
        return prev;
      });
    },
    []
  );

  const handleAgentServerChatCreated = useCallback((row: AgentChatListItem) => {
    setAgentServerChatId(row.id);
    setAgentChatRows((prev) => {
      const rest = prev.filter((c) => c.id !== row.id);
      // Оптимистично: только что созданный чат — первым, пока не пришёл полный список с сервера.
      return [row, ...sortAgentChatsNewestFirst(rest)];
    });
  }, []);

  const handleAgentChatAccessLost = useCallback(() => {
    setAgentServerChatId(null);
    setAgentUiSessionId(generateUUID());
    void fetchAgentChatsFirst({ silent: true });
  }, [fetchAgentChatsFirst]);

  const handleNewChat = useCallback(() => {
    setAgentServerChatId(null);
    setAgentUiSessionId(generateUUID());
  }, []);

  const handleForwardMessage = useCallback(
    (msg: MessengerMessage) => {
      forwardOriginThreadIdRef.current = activeThreadId;
      setForwardMsg(msg);
      setForwardMsgsModal([]);
    },
    [activeThreadId]
  );

  const handleForwardMessages = useCallback(
    (msgs: MessengerMessage[]) => {
      forwardOriginThreadIdRef.current = activeThreadId;
      setForwardMsgsModal(msgs);
      setForwardMsg(null);
    },
    [activeThreadId]
  );

  const forwardModalOpen = forwardMsg !== null || forwardMsgsModal.length > 0;

  useEffect(() => {
    if (!forwardModalOpen) return;
    void ensureSavedThread();
  }, [forwardModalOpen, ensureSavedThread]);

  const consumePendingForwardForThread = useCallback((tid: string) => {
    setPendingForwardsByThread((prev) => {
      if (!prev[tid]) return prev;
      const next = { ...prev };
      delete next[tid];
      return next;
    });
  }, []);

  const handleForwardToThread = useCallback(
    (payload: {
      threadId: string;
      peerId: string;
      peerName: string;
      threadKind?: 'direct' | 'group';
    }) => {
      const msgs =
        forwardMsgsModal.length > 0 ? forwardMsgsModal : forwardMsg ? [forwardMsg] : [];
      if (msgs.length === 0) return;

      const origin = forwardOriginThreadIdRef.current;
      forwardOriginThreadIdRef.current = null;
      // Сброс выделения в исходном треде до смены activeContact: иначе один батч размонтирует чат
      // до срабатывания эффекта в DirectThreadView.
      if (origin) {
        forwardClearNonceRef.current += 1;
        flushSync(() => {
          setForwardSelectionClearSignal({
            nonce: forwardClearNonceRef.current,
            threadId: origin,
          });
        });
      }

      ensureDirectThreadSlot(payload.threadId);
      setPendingForwardsByThread((prev) => ({ ...prev, [payload.threadId]: msgs }));

      if (payload.threadKind === 'group') {
        setActiveContact({
          type: 'group',
          threadId: payload.threadId,
          title: payload.peerName,
        });
      } else if (payload.threadKind === 'direct' || payload.threadKind === undefined) {
        setActiveContact({
          type: 'direct',
          threadId: payload.threadId,
          peerId: payload.peerId,
          peerName: payload.peerName,
        });
      }

      setForwardMsg(null);
      setForwardMsgsModal([]);
    },
    [forwardMsgsModal, forwardMsg]
  );

  const activeSubtractPeerId =
    messengerReadReceiptsActive &&
    activeContact !== 'agent' &&
    activeContact !== 'saved' &&
    activeContact !== null &&
    activeContact.type === 'direct'
      ? activeContact.peerId
      : null;

  const activeSubtractGroupThreadId =
    messengerReadReceiptsActive &&
    activeContact !== 'agent' &&
    activeContact !== 'saved' &&
    activeContact !== null &&
    activeContact.type === 'group'
      ? activeContact.threadId
      : null;

  const onVisibleIncomingPeerMessages = useCallback((count: number) => {
    if (count <= 0) return;
    setSidebarUnreadSubtractCount((c) => {
      const next = c + count;
      setMessengerSidebarUnreadViewportSubtract({
        peerId: activeSubtractPeerId,
        threadId: activeSubtractGroupThreadId,
        count: next,
      });
      return next;
    });
  }, [activeSubtractPeerId, activeSubtractGroupThreadId]);

  useEffect(() => {
    setSidebarUnreadSubtractCount(0);
    setMessengerSidebarUnreadViewportSubtract({
      peerId: activeSubtractPeerId,
      threadId: activeSubtractGroupThreadId,
      count: 0,
    });
  }, [activeSubtractPeerId, activeSubtractGroupThreadId]);

  useEffect(() => {
    if (!messengerReadReceiptsActive) {
      setSidebarUnreadSubtractCount(0);
      setMessengerSidebarUnreadViewportSubtract({ peerId: null, threadId: null, count: 0 });
    }
  }, [messengerReadReceiptsActive]);

  useEffect(() => {
    if (!activeSubtractPeerId) return;
    const t = threads.find((th) => th.thread_kind === 'direct' && th.peer_id === activeSubtractPeerId);
    if (t && t.unread_count === 0) {
      setSidebarUnreadSubtractCount(0);
      setMessengerSidebarUnreadViewportSubtract({
        peerId: activeSubtractPeerId,
        threadId: activeSubtractGroupThreadId,
        count: 0,
      });
    }
  }, [threads, activeSubtractPeerId, activeSubtractGroupThreadId]);

  useEffect(() => {
    if (!activeSubtractGroupThreadId) return;
    const t = threads.find((th) => th.id === activeSubtractGroupThreadId);
    if (t && t.unread_count === 0) {
      setSidebarUnreadSubtractCount(0);
      setMessengerSidebarUnreadViewportSubtract({
        peerId: activeSubtractPeerId,
        threadId: activeSubtractGroupThreadId,
        count: 0,
      });
    }
  }, [threads, activeSubtractGroupThreadId, activeSubtractPeerId]);

  const activeDirectPeerId =
    activeContact !== 'agent' &&
    activeContact !== 'saved' &&
    activeContact !== null &&
    activeContact.type === 'direct'
      ? activeContact.peerId
      : null;

  const directFabUnreadCount = useMemo(
    () =>
      activeDirectPeerId
        ? displayedMessengerUnreadForPeer(
            threads,
            activeDirectPeerId,
            activeSubtractPeerId,
            sidebarUnreadSubtractCount
          )
        : 0,
    [threads, activeDirectPeerId, activeSubtractPeerId, sidebarUnreadSubtractCount]
  );

  const isDirectActive =
    activeContact !== 'agent' &&
    activeContact !== 'saved' &&
    activeContact !== null &&
    activeContact.type === 'direct' &&
    Boolean(activeContact.threadId);

  const isGroupActive =
    activeContact !== 'agent' &&
    activeContact !== 'saved' &&
    activeContact !== null &&
    activeContact.type === 'group';

  const activeGroupMyRole = useMemo(() => {
    if (!isGroupActive) return 'member' as const;
    if (activeContact == null || activeContact.type !== 'group') return 'member' as const;
    const t = threads.find((th) => th.id === activeContact.threadId);
    return t?.my_member_role === 'admin' ? 'admin' : 'member';
  }, [isGroupActive, activeContact, threads]);

  const groupFabUnreadCount = useMemo(() => {
    if (!isGroupActive) return 0;
    if (activeContact == null || activeContact.type !== 'group') return 0;
    return displayedMessengerUnreadForThread(
      threads,
      activeContact.threadId,
      activeSubtractGroupThreadId,
      sidebarUnreadSubtractCount
    );
  }, [
    isGroupActive,
    activeContact,
    threads,
    activeSubtractGroupThreadId,
    sidebarUnreadSubtractCount,
  ]);

  const agentHasProcessingFromStore = useAnyAgentChatProcessing();
  const agentHasProcessing = agentHasProcessingFromStore ||
    Object.values(agentProcessingByChatId).some(Boolean);
  const activeAgentChatProcessing = useMemo(() => {
    if (!agentServerChatId) return false;
    return Boolean(agentProcessingByChatId[agentServerChatId]);
  }, [agentServerChatId, agentProcessingByChatId]);

  const agentChatsSidebarMenu = useMemo(
    () => (
      <AgentChatsSection
        chats={agentChatRows}
        exitingChatId={exitingAgentChatId}
        processingByChatId={agentProcessingByChatId}
        hasMore={agentChatsHasMore}
        loading={agentChatsLoading}
        loadingMore={agentChatsLoadingMore}
        activeServerChatId={agentServerChatId}
        isDraftActive={agentServerChatId === null}
        onSelectChat={selectPersistedAgentChat}
        onDeleteChat={handleDeleteAgentChat}
        onRenameChat={handleRenameAgentChat}
        onLoadMore={fetchAgentChatsMore}
        onOpen={fetchAgentChatsFirst}
      />
    ),
    [
      agentChatRows,
      exitingAgentChatId,
      agentProcessingByChatId,
      agentChatsHasMore,
      agentChatsLoading,
      agentChatsLoadingMore,
      agentServerChatId,
      selectPersistedAgentChat,
      handleDeleteAgentChat,
      handleRenameAgentChat,
      fetchAgentChatsMore,
      fetchAgentChatsFirst,
    ]
  );

  const isSavedActive = activeContact === 'saved';

  return (
    <div className="chatShell">
      <ChatSidebar
        users={users}
        threads={threads}
        activeContact={activeContact}
        onSelect={handleSelectContact}
        currentUserId={currentUserId}
        sidebarUnreadSubtractPeerId={activeSubtractPeerId}
        sidebarUnreadSubtractThreadId={activeSubtractGroupThreadId}
        sidebarUnreadSubtractCount={sidebarUnreadSubtractCount}
        onOpenCreateGroup={() => setCreateGroupOpen(true)}
        onSelectMessageResult={handleSelectMessageSearchResult}
        onSidebarSearchHighlightWordsChange={handleMessengerSidebarSearchHighlightWords}
        agentChatsMenu={agentChatsSidebarMenu}
        agentChatsMenuMountOpen={agentChatsSectionOpen}
        agentUnreadTotal={agentTotalUnread}
        agentHasProcessing={agentHasProcessing}
      />
      <div className="chatShell__main">
        <div
          className={`chatShell__agentPane${activeContact !== 'agent' ? ' chatShell__agentPane--inactive' : ''}`}
          aria-hidden={activeContact !== 'agent'}
        >
          <AgentChatView
            uiSessionId={agentUiSessionId}
            serverChatId={agentServerChatId}
            onServerChatCreated={handleAgentServerChatCreated}
            onServerChatAccessLost={handleAgentChatAccessLost}
            onRefreshAgentChatsList={refreshAgentChatsListQuiet}
            onProcessingStateChange={setAgentChatProcessing}
            processingExternal={activeAgentChatProcessing}
            onNewChat={handleNewChat}
            onClose={onClose}
            onForwardMessage={handleForwardMessage}
            paneActive={activeContact === 'agent'}
            messengerSurfaceVisible={messengerSurfaceVisible}
            persistedListTitle={agentOpenPersistedTitle}
            persistedListModelSlot={agentOpenPersistedModelSlot}
          />
        </div>

        {isGroupActive && activeContact != null && activeContact.type === 'group' && (
            <GroupThreadView
              threadId={activeContact.threadId}
              title={activeContact.title}
              myMemberRole={activeGroupMyRole}
              fabUnreadCount={groupFabUnreadCount}
              onClose={onClose}
              readReceiptsActive={messengerReadReceiptsActive}
              messengerSurfaceVisible={messengerSurfaceVisible}
              onVisibleIncomingPeerMessages={onVisibleIncomingPeerMessages}
              onForwardMessage={handleForwardMessage}
              onForwardMessages={handleForwardMessages}
              initialForwardMsgs={
                activeThreadId && pendingForwardsByThread[activeThreadId]?.length
                  ? pendingForwardsByThread[activeThreadId]!
                  : MESSENGER_EMPTY_FORWARD_DRAFT
              }
              onConsumePendingForwardDraft={() => {
                if (activeThreadId) consumePendingForwardForThread(activeThreadId);
              }}
              forwardSelectionClearSignal={forwardSelectionClearSignal}
              onThreadMessageCommitted={handleThreadMessageCommitted}
              users={users}
              onThreadsRefresh={fetchThreads}
              onGroupRemoved={handleGroupThreadRemoved}
              jumpToMessage={
                messageJumpIntent?.threadId === activeContact.threadId ? messageJumpIntent : null
              }
              onJumpToMessageSettled={handleJumpToMessageSettled}
              sidebarSearchHighlightWords={messengerSidebarSearchHighlightWords}
            />
          )}

        {isDirectActive && activeContact != null && activeContact.type === 'direct' && (
          <DirectThreadView
            threadId={activeContact.threadId}
            peerId={activeContact.peerId}
            peerName={activeContact.peerName}
            fabUnreadCount={directFabUnreadCount}
            onClose={onClose}
            readReceiptsActive={messengerReadReceiptsActive}
            messengerSurfaceVisible={messengerSurfaceVisible}
            onVisibleIncomingPeerMessages={onVisibleIncomingPeerMessages}
            onForwardMessage={handleForwardMessage}
            onForwardMessages={handleForwardMessages}
            initialForwardMsgs={
              activeThreadId && pendingForwardsByThread[activeThreadId]?.length
                ? pendingForwardsByThread[activeThreadId]!
                : MESSENGER_EMPTY_FORWARD_DRAFT
            }
            onConsumePendingForwardDraft={() => {
              if (activeThreadId) consumePendingForwardForThread(activeThreadId);
            }}
            allThreads={threads}
            forwardSelectionClearSignal={forwardSelectionClearSignal}
            onThreadMessageCommitted={handleThreadMessageCommitted}
            jumpToMessage={
              messageJumpIntent?.threadId === activeContact.threadId ? messageJumpIntent : null
            }
            onJumpToMessageSettled={handleJumpToMessageSettled}
            sidebarSearchHighlightWords={messengerSidebarSearchHighlightWords}
          />
        )}

        {isSavedActive && savedThread && (
          <DirectThreadView
            threadId={savedThread.id}
            peerId={currentUserId ?? ''}
            peerName="Сохранённые сообщения"
            fabUnreadCount={0}
            onClose={onClose}
            readReceiptsActive={messengerReadReceiptsActive}
            messengerSurfaceVisible={messengerSurfaceVisible}
            onVisibleIncomingPeerMessages={onVisibleIncomingPeerMessages}
            onForwardMessage={handleForwardMessage}
            onForwardMessages={handleForwardMessages}
            initialForwardMsgs={
              pendingForwardsByThread[savedThread.id]?.length
                ? pendingForwardsByThread[savedThread.id]!
                : MESSENGER_EMPTY_FORWARD_DRAFT
            }
            onConsumePendingForwardDraft={() => consumePendingForwardForThread(savedThread.id)}
            allThreads={threads}
            isSavedThread
            forwardSelectionClearSignal={forwardSelectionClearSignal}
            onThreadMessageCommitted={handleThreadMessageCommitted}
            jumpToMessage={
              messageJumpIntent?.threadId === savedThread.id ? messageJumpIntent : null
            }
            onJumpToMessageSettled={handleJumpToMessageSettled}
            sidebarSearchHighlightWords={messengerSidebarSearchHighlightWords}
          />
        )}

        {activeContact === null && onClose ? <ChatShellEmptyPane onClose={onClose} /> : null}

        {isSavedActive && !savedThread && (
          <div
            className="chatShell__loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Загрузка сохранённых сообщений"
          >
            <span className="chatShell__loadingSpinner" />
          </div>
        )}

        {activeContact !== 'agent' &&
          activeContact !== 'saved' &&
          activeContact !== null &&
          activeContact.type === 'direct' &&
          !activeContact.threadId && (
            <div
              className="chatShell__loading"
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label="Создание чата"
            >
              <span className="chatShell__loadingSpinner" />
            </div>
          )}
      </div>

      <ForwardMessageModal
        open={forwardModalOpen}
        message={forwardMsg}
        messages={forwardMsgsModal.length > 0 ? forwardMsgsModal : undefined}
        users={users}
        threads={threads}
        savedThread={savedThread}
        savedThreadLoading={savedThreadLoading}
        currentUserId={currentUserId}
        onClose={() => {
          setForwardMsg(null);
          setForwardMsgsModal([]);
        }}
        onForward={handleForwardToThread}
        onThreadsRefresh={fetchThreads}
      />

      <CreateGroupChatModal
        open={createGroupOpen}
        users={users}
        currentUserId={currentUserId}
        onClose={() => setCreateGroupOpen(false)}
        onCreated={(thread) => {
          ensureDirectThreadSlot(thread.id);
          const label = thread.title?.trim() ? thread.title.trim() : 'Группа';
          setActiveContact({ type: 'group', threadId: thread.id, title: label });
        }}
        onThreadsRefresh={fetchThreads}
      />
    </div>
  );
}
